import type { PartCategory } from '@/types'

/**
 * Master parts catalogue for the builder (Parts step — Step 5).
 *
 * Frame selection is handled by the wizard (Steps 1–4) and is NOT in this array.
 * Single source of truth for all builder options.
 * Never import this directly in components — consume via BuilderContext.
 *
 * Categories with subcategories have options: [].
 *
 * Sourced from the six component spreadsheets in src/Component Spreadsheets/
 * (forks, rear shocks, wheels, tyres, drivetrain, brakes — research date 2026-07-02).
 * Only options marked ready / include-now in the sheets are listed here.
 */
export const PARTS: PartCategory[] = [

  // ─── Forks ───────────────────────────────────────────────────────────────────
  {
    id: 'forks',
    label: 'Forks',
    description: 'Suspension fork spec defines your travel, geometry, and feel. Matched to your frame model and riding discipline.',
    options: [
      {
        id: 'fork-fox-38-factory-gripx2', price: 2600, weightG: 2400,
        label: 'Fox 38 Factory GRIP X2',
        description: '160–180mm travel. GRIP X2 damper with FLOAT EVOL GlideCore air spring, Kashima-coated 38mm stanchions, Kabolt X axle. The premium enduro default.',
        material: 'alloy',
        size: '29" / 160–180mm',
        finish: 'Kashima',
        imageUrl: '/images/builder/forks/fox_38_factory_grip_x2_side.png',
        priceIndicator: 'premium',
      },
      {
        id: 'fork-rockshox-zeb-ultimate', price: 2100, weightG: 2341,
        label: 'RockShox ZEB Ultimate Charger 3.1',
        description: '150–190mm travel. Charger 3.1 RC2 damper with DebonAir+ spring on a 38mm chassis. Approx. 2341g — the safest high-end enduro choice.',
        material: 'alloy',
        size: '29" / 150–190mm',
        finish: 'Gloss Black',
        imageUrl: '/images/builder/forks/rockshox_zeb_ultimate_side.png',
        priceIndicator: 'premium',
      },
      {
        id: 'fork-rockshox-boxxer-ultimate', price: 3300, weightG: 2900,
        label: 'RockShox BoXXer Ultimate',
        description: '180–200mm dual-crown downhill fork. Charger 3 RC2 damper, DebonAir+ spring, 20x110 Boost axle. Built for race tracks and bike parks.',
        material: 'alloy',
        size: '29" / 200mm',
        finish: 'Electric Red',
        imageUrl: '/images/builder/forks/rockshox_boxxer_ultimate_side.png',
        priceIndicator: 'premium',
      },
      {
        id: 'fork-fox-36-factory-gripx2', price: 2500, weightG: 2100,
        label: 'Fox 36 Factory GRIP X2',
        description: '150–160mm travel. GRIP X2 damper with Kashima-coated 36mm stanchions. Lighter feel than a 38 or ZEB for aggressive trail and light enduro.',
        material: 'alloy',
        size: '29" / 150–160mm',
        finish: 'Kashima',
        imageUrl: '/images/builder/forks/fox_36_factory_grip_x2_side.png',
        priceIndicator: 'upgrade',
      },
      {
        id: 'fork-rockshox-lyrik-ultimate', price: 1900, weightG: 2028,
        label: 'RockShox Lyrik Ultimate Charger 3.1',
        description: '140–160mm travel. Charger 3.1 RC2 damper, DebonAir+ spring, 35mm chassis. Approx. 2028g — the strong trail/enduro option below the ZEB.',
        material: 'alloy',
        size: '29" / 140–160mm',
        finish: 'Gloss Black',
        imageUrl: '/images/builder/forks/rockshox_lyrik_ultimate_side.png',
        priceIndicator: 'upgrade',
      },
      {
        id: 'fork-rockshox-pike-ultimate', price: 1800, weightG: 1887,
        label: 'RockShox Pike Ultimate Charger 3.1',
        description: '120–140mm travel. Charger 3.1 RC2 damper, DebonAir+ spring. Approx. 1887g — the premium down-country and trail choice.',
        material: 'alloy',
        size: '29" / 120–140mm',
        finish: 'Gloss Black',
        imageUrl: '/images/builder/forks/rockshox_pike_ultimate_side.png',
        priceIndicator: 'upgrade',
      },
    ],
  },

  // ─── Shocks ───────────────────────────────────────────────────────────────────
  {
    id: 'shocks',
    label: 'Shocks',
    description: 'Rear shock tuned to your frame\'s linkage and your riding weight and style. Air or coil — both available. Coil spring rate specified separately at order.',
    options: [
      {
        id: 'shock-fox-float-x2-factory', price: 1300, weightG: 660,
        label: 'Fox Float X2 Factory',
        description: 'Air shock with HSC/LSC/HSR/LSR damping and 2-position adjust. Volume-spacer tuneable, max 300psi. The top-tier gravity air option.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Kashima',
        imageUrl: '/images/builder/shocks/fox_float_x2_factory.png',
        priceIndicator: 'premium',
      },
      {
        id: 'shock-fox-dhx2-factory', price: 1300, weightG: 920,
        label: 'Fox DHX2 Factory',
        description: 'Coil shock with four-way HSC/LSC/HSR/LSR damping. 568g without spring. The proper Fox coil for DH and premium enduro builds.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Kashima',
        imageUrl: '/images/builder/shocks/fox_dhx2_factory.png',
        priceIndicator: 'premium',
      },
      {
        id: 'shock-rockshox-vivid-ultimate', price: 1100, weightG: 670,
        label: 'RockShox Vivid Ultimate',
        description: 'Air shock with TouchDown RC2T damper, DebonAir+ spring, and adjustable hydraulic bottom out. 670g. Built for aggressive enduro and DH-style bikes.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Gloss Black',
        imageUrl: '/images/builder/shocks/rockshox_vivid_ultimate_air.png',
        priceIndicator: 'premium',
      },
      {
        id: 'shock-rockshox-super-deluxe-ultimate', price: 900, weightG: 458,
        label: 'RockShox Super Deluxe Ultimate',
        description: 'Air shock with RC2T damper, hydraulic bottom out, and threshold adjust. 458g and e-bike approved. The default premium air option.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Gloss Black',
        imageUrl: '/images/builder/shocks/rockshox_super_deluxe_ultimate_air.png',
        priceIndicator: 'upgrade',
      },
      {
        id: 'shock-ohlins-ttx22m-coil', price: 1500, weightG: 1000,
        label: 'Öhlins TTX22m.2 Coil',
        description: 'TTX twin-tube coil damper with 3 HSC, 16 LSC, and 7 rebound clicks. Stroke adjustable with spacers. The boutique race coil.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Black / Gold',
        imageUrl: '/images/builder/shocks/ohlins_ttx22m2_coil.png',
        priceIndicator: 'premium',
      },
      {
        id: 'shock-ohlins-ttx2air', price: 1400, weightG: 630,
        label: 'Öhlins TTX2Air m.2',
        description: 'Double-can air shock with TTX damper, HSC/LSC/rebound, and climb mode. Max 325psi. Premium air performance with the Öhlins look.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Black / Gold',
        imageUrl: '/images/builder/shocks/ohlins_ttx2air_m2.png',
        priceIndicator: 'premium',
      },
      {
        id: 'shock-ext-storia-v4', price: 2000, weightG: 800,
        label: 'EXT Storia V4 LOK',
        description: 'Hand-built Italian coil with 4-way LSC/HSC/LSR/HBC damping and LOK 2.0 climb lockout. 397g without spring. Ultra high-end boutique option.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Anodised Black',
        imageUrl: '/images/builder/shocks/ext_storia_v4.png',
        priceIndicator: 'premium',
      },
      {
        id: 'shock-cane-creek-kitsuma-coil', price: 1100, weightG: 950,
        label: 'Cane Creek Kitsuma Coil G2',
        description: 'Coil shock with tool-free four-way damping adjustment and climb switch. Very tuneable with a broad size compatibility matrix.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Black',
        imageUrl: '/images/builder/shocks/cane_creek_kitsuma_coil.png',
        priceIndicator: 'upgrade',
      },
      {
        id: 'shock-dvo-jade-x', price: 800, weightG: 970,
        label: 'DVO Jade X',
        description: 'Coil shock with T3 compression adjust, rebound, and bladder pressure tuning. Practical value-premium coil for hard riding.',
        material: 'alloy',
        size: '230×65mm',
        finish: 'Black / Green',
        imageUrl: '/images/builder/shocks/dvo_jade_x.png',
        priceIndicator: 'included',
      },
    ],
  },

  // ─── Wheels ──────────────────────────────────────────────────────────────────
  {
    id: 'wheels',
    label: 'Wheels',
    description: 'Complete high-end wheelsets matched to your frame, plus tyres specified front and rear. Hub, freehub, and spacing confirmed at order.',
    options: [],
    subcategories: [
      {
        id: 'wheelset',
        label: 'Wheelset',
        options: [
          { id: 'wheelset-nobl-tr37', price: 3400, weightG: 1850, label: 'NOBL TR37 Carbon', description: 'Boutique carbon enduro wheel, 31/30mm internal, 32h. Chris King, DT Swiss, Industry Nine, or Onyx hub options. The premium enduro default.', material: 'carbon', size: '27.5" / 29"', finish: 'Raw Carbon / NOBL Decals', imageUrl: '/images/builder/wheels/nobl_tr37_carbon.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-nobl-tr41', price: 3500, weightG: 1950, label: 'NOBL TR41 Carbon', description: 'Wider, tougher NOBL at 35/34mm internal for e-bikes, heavy riders, and 2.4–2.8" tyres. Same premium hub menu as the TR37.', material: 'carbon', size: '27.5" / 29"', finish: 'Raw Carbon / NOBL Decals', imageUrl: '/images/builder/wheels/nobl_tr41_carbon.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-reserve-30hd', price: 3000, weightG: 1791, label: 'Reserve 30|HD Carbon', description: '30mm internal enduro wheelset, 28h, e-bike approved. DT Swiss 350 or e*thirteen Sidekick hubs, ~1791g. Clean, strong, and easy to trust.', material: 'carbon', size: '29" / MX', finish: 'Black / Reserve Decals', imageUrl: '/images/builder/wheels/reserve_30_hd_carbon.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-reserve-31dh', price: 3200, weightG: 2006, label: 'Reserve 31|DH Carbon', description: 'Proper DH wheel — 31mm internal, 32h, 20x110 front / 12x157 rear spacing. Industry Nine Hydra or DT 350 hubs, ~2006g.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / Reserve Decals', imageUrl: '/images/builder/wheels/reserve_31_dh_carbon.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-reserve-30hd-31dh', price: 3100, weightG: 1933, label: 'Reserve 30|HD / 31|DH Carbon', description: 'Mixed-strength set: lighter 30|HD front feel with tougher 31|DH rear. Industry Nine 1/1 hubs, ~1933g. Great e-bike and hard-charger mix.', material: 'carbon', size: '29"', finish: 'Black / Reserve Decals', imageUrl: '/images/builder/wheels/reserve_30_hd_31_dh_carbon.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-wao-triad', price: 3600, weightG: 1900, label: 'We Are One Convergence Triad', description: 'Canadian-made carbon enduro rim, 30mm internal, 28h/32h, built through Industry Nine. Very boutique, very Burton.', material: 'carbon', size: '27.5" / 29"', finish: 'Black Carbon', imageUrl: '/images/builder/wheels/we_are_one_convergence_triad.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-i9-hydra-enduro-s', price: 2200, weightG: 1990, label: 'Industry Nine Hydra Enduro S', description: 'Alloy enduro wheelset, 30.5mm internal, 28h straight-pull on Hydra hubs. The best option for colour-matched anodised hubs and spokes.', material: 'alloy', size: '27.5" / 29" / MX', finish: 'Anodised Colour Options', imageUrl: '/images/builder/wheels/industry_nine_hydra_enduro_s.jpg', priceIndicator: 'upgrade' },
          { id: 'wheelset-i9-solix-xc', price: 3800, weightG: 1390, label: 'Industry Nine Solix XC Carbon', description: 'XC/down-country carbon wheelset, 30mm internal, 24h 2:1 lacing on Solix M hubs. Sub-1400g class — light and fast, not a park wheel.', material: 'carbon', size: '29"', finish: 'Black / I9 Anodised Accents', imageUrl: '/images/builder/wheels/industry_nine_solix_xc_carbon.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-enve-m9-pro', price: 4600, weightG: 2200, label: 'ENVE M9 Pro', description: 'Flagship gravity wheelset with INNERDRIVE hubs, 29mm internal, 28/32h. 20x110 and 12x157 options for DH builds. ~2200g.', material: 'carbon', size: '29" / 27.5" rear option', finish: 'Black / ENVE Decals', imageUrl: '/images/builder/wheels/enve_m9_pro.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-enve-am30', price: 4200, weightG: 1880, label: 'ENVE AM30', description: 'All-mountain/enduro carbon wheelset, 30mm internal, 28h. Cleaner and less extreme than the M9 — the premium ENVE trail choice. ~1880g.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / ENVE Decals', imageUrl: '/images/builder/wheels/enve_am30.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-dt-exc-1200', price: 4400, weightG: 1644, label: 'DT Swiss EXC 1200 Classic', description: 'Race-spec enduro carbon wheelset on DT 180 hubs with Ratchet DEG 90. From 1644g. Proven race kit rather than boutique colour.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / DT Decals', imageUrl: '/images/builder/wheels/dt_swiss_exc_1200_classic.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-dt-xmc-1200', price: 4000, weightG: 1485, label: 'DT Swiss XMC 1200 Spline', description: 'Fast trail/all-mountain carbon wheelset, 30mm internal, from 1485g on DT 180 hubs. Better suited to fast trail than gravity.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / DT Decals', imageUrl: '/images/builder/wheels/dt_swiss_xmc_1200_spline.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-dt-hxc-1501', price: 3200, weightG: 1691, label: 'DT Swiss HXC 1501 Spline One LS', description: 'Purpose-built eMTB carbon wheelset with DT 240 LS hubs, Ratchet EXP OS 30, and hybrid spokes. From 1691g.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / DT Decals', imageUrl: '/images/builder/wheels/dt_swiss_hxc_1501_spline_one_ls.jpg', priceIndicator: 'premium' },
          { id: 'wheelset-cb-synthesis-enduro', price: 2800, weightG: 1900, label: 'Crankbrothers Synthesis Enduro Carbon', description: 'Front/rear-specific design: wider, compliant 31.5mm front and stiffer 29.5mm rear. A genuinely different technical story.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / Silver Detail', imageUrl: '/images/builder/wheels/crankbrothers_synthesis_enduro_carbon.png', priceIndicator: 'upgrade' },
          { id: 'wheelset-zipp-3zero-moto', price: 2600, weightG: 1980, label: 'Zipp 3ZERO MOTO', description: 'Single-wall moto-inspired carbon rim, 30mm internal, 32h. Tuned compliance for aggressive technical riding.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / Speed Line Graphics', imageUrl: '/images/builder/wheels/zipp_3zero_moto.jpg', priceIndicator: 'upgrade' },
          { id: 'wheelset-roval-traverse-hd', price: 2400, weightG: 1706, label: 'Roval Traverse HD', description: 'Strong carbon trail/enduro wheelset, 30mm internal, 24mm deep, on DT Swiss 240 or 350 hubs. ~1706g with tape and valves.', material: 'carbon', size: '29" / 27.5" rear option', finish: 'Satin Carbon / Gloss Black', imageUrl: '/images/builder/wheels/roval_traverse_hd_240_350.jpg', priceIndicator: 'upgrade' },
          { id: 'wheelset-raceface-era', price: 2900, weightG: 1656, label: 'Race Face Era Carbon', description: 'Carbon enduro wheelset on Vault hubs, 30mm internal, 28h. Broad spacing menu including 20x110 and 12x157 Super Boost. From 1656g.', material: 'carbon', size: '27.5" / 29"', finish: 'Black / Grey Decals', imageUrl: '/images/builder/wheels/race_face_era_carbon.jpg', priceIndicator: 'upgrade' },
          { id: 'wheelset-raceface-turbine', price: 1500, weightG: 1766, label: 'Race Face Turbine Alloy', description: '6069 alloy wheelset on Vault hubs, 30mm internal. Durable, rebuildable, and sensible — the tough lower-cost choice. From 1766g.', material: 'alloy', size: '27.5" / 29"', finish: 'Black', imageUrl: '/images/builder/wheels/race_face_turbine_alloy.jpg', priceIndicator: 'included' },
        ],
      },
      {
        id: 'tires',
        label: 'Tyres',
        options: [
          { id: 'tires-maxxis-assegai', price: 260, weightG: 2500, label: 'Maxxis Assegai', description: 'Default premium front tyre for enduro, DH, and park. EXO+ to DH casings, MaxxTerra or MaxxGrip compounds. Huge grip, safe choice.', material: 'alloy', size: '2.5 WT / 2.6"', finish: 'Black / Tanwall', imageUrl: '/images/builder/tyres/maxxis_assegai_black.png', priceIndicator: 'included' },
          { id: 'tires-maxxis-minion-dhf', price: 240, weightG: 2200, label: 'Maxxis Minion DHF', description: 'The classic aggressive front tyre. Ramped centre knobs with strong side knobs, EXO to DH casings.', material: 'alloy', size: '2.3 / 2.5 WT', finish: 'Black / Tanwall', imageUrl: '/images/builder/tyres/maxxis_minion_dhf_black.png', priceIndicator: 'included' },
          { id: 'tires-maxxis-dhr2', price: 240, weightG: 2200, label: 'Maxxis Minion DHR II', description: 'Best default rear tyre. Strong braking and cornering support, EXO to DH casings, MaxxTerra or MaxxGrip compounds.', material: 'alloy', size: '2.4 WT / 2.5 WT', finish: 'Black / Tanwall', imageUrl: '/images/builder/tyres/maxxis_minion_dhr_ii_black.png', priceIndicator: 'included' },
          { id: 'tires-maxxis-dissector', price: 240, weightG: 2000, label: 'Maxxis Dissector', description: 'Faster-rolling rear for fast enduro and dry trail when the DHR II feels too draggy. EXO+ or DoubleDown casing.', material: 'alloy', size: '2.4 WT / 2.6"', finish: 'Black', imageUrl: '/images/builder/tyres/maxxis_dissector_placeholder.png', priceIndicator: 'included' },
          { id: 'tires-maxxis-shorty', price: 240, weightG: 2100, label: 'Maxxis Shorty', description: 'Wet, loose, mud, and winter specialist spike. 3C MaxxTerra or MaxxGrip, EXO to DH casings.', material: 'alloy', size: '2.4 WT', finish: 'Black', imageUrl: '/images/builder/tyres/maxxis_shorty_black.png', priceIndicator: 'included' },
          { id: 'tires-maxxis-aggressor', price: 220, weightG: 2000, label: 'Maxxis Aggressor', description: 'Fast rear tyre for dry, rocky conditions. Dual compound with EXO or DoubleDown casing.', material: 'alloy', size: '2.3 / 2.5 WT', finish: 'Black', imageUrl: '/images/builder/tyres/maxxis_aggressor_black.png', priceIndicator: 'included' },
          { id: 'tires-conti-kryptotal-fr', price: 260, weightG: 2300, label: 'Continental Kryptotal Fr', description: 'Modern premium front for enduro and DH mixed terrain. Trail, Enduro, or Downhill casing with Soft and SuperSoft compounds.', material: 'alloy', size: '2.4"', finish: 'Black', imageUrl: '/images/builder/tyres/continental_kryptotal_fr.png', priceIndicator: 'included' },
          { id: 'tires-conti-kryptotal-re', price: 260, weightG: 2300, label: 'Continental Kryptotal Re', description: 'Matching rear for the Kryptotal Fr. Trail, Enduro, or Downhill casing with Endurance to SuperSoft compounds.', material: 'alloy', size: '2.4"', finish: 'Black', imageUrl: '/images/builder/tyres/continental_kryptotal_re.png', priceIndicator: 'included' },
          { id: 'tires-conti-argotal', price: 260, weightG: 2250, label: 'Continental Argotal', description: 'Loose soil, loam, and wet-grip front option. Soft and SuperSoft compounds in Trail to Downhill casings.', material: 'alloy', size: '2.4"', finish: 'Black', imageUrl: '/images/builder/tyres/continental_argotal.png', priceIndicator: 'included' },
          { id: 'tires-conti-xynotal', price: 260, weightG: 2200, label: 'Continental Xynotal', description: 'Faster-rolling rear for hardpack and rocky, dry terrain. Endurance to SuperSoft compounds.', material: 'alloy', size: '2.4"', finish: 'Black', imageUrl: '/images/builder/tyres/continental_xynotal.png', priceIndicator: 'included' },
          { id: 'tires-schwalbe-magic-mary', price: 280, weightG: 2500, label: 'Schwalbe Magic Mary', description: 'High-grip gravity front. ADDIX Soft or Ultra Soft in Super Trail, Super Gravity, or Super Downhill casings.', material: 'alloy', size: '2.4 / 2.6"', finish: 'Black / Bronze', imageUrl: '/images/builder/tyres/schwalbe_magic_mary_placeholder.png', priceIndicator: 'upgrade' },
          { id: 'tires-schwalbe-big-betty', price: 280, weightG: 2450, label: 'Schwalbe Big Betty', description: 'Rear match for the Magic Mary. ADDIX Soft or Ultra Soft in Super Trail to Super Downhill casings.', material: 'alloy', size: '2.4 / 2.6"', finish: 'Black / Bronze', imageUrl: '/images/builder/tyres/schwalbe_big_betty_placeholder.png', priceIndicator: 'upgrade' },
          { id: 'tires-vittoria-mazza', price: 260, weightG: 2200, label: 'Vittoria Mazza Enduro', description: 'Boutique mixed-terrain front with 4C Graphene compound and Enduro TLR casing.', material: 'alloy', size: '2.4 / 2.6"', finish: 'Black', imageUrl: '/images/builder/tyres/vittoria_mazza_enduro.png', priceIndicator: 'upgrade' },
          { id: 'tires-vittoria-martello', price: 260, weightG: 2250, label: 'Vittoria Martello Enduro', description: 'Dry and mixed-terrain rear match for the Mazza. 4C Graphene compound, Enduro TLR casing.', material: 'alloy', size: '2.4 / 2.6"', finish: 'Black', imageUrl: '/images/builder/tyres/vittoria_martello_enduro.png', priceIndicator: 'upgrade' },
          { id: 'tires-vittoria-mota', price: 280, weightG: 2300, label: 'Vittoria Mota Enduro Race', description: 'Wet and loose race front with Graphene + Silica Race compound and Enduro Race casing.', material: 'alloy', size: '2.4 / 2.6"', finish: 'Black', imageUrl: '/images/builder/tyres/vittoria_mota_enduro_race.png', priceIndicator: 'upgrade' },
          { id: 'tires-specialized-cannibal', price: 240, weightG: 2500, label: 'Specialized Cannibal Grid Gravity T9', description: 'Proper gravity tyre in Grid Gravity 2-ply casing with Gripton T9 compound. Front or rear.', material: 'alloy', size: '2.4"', finish: 'Black', imageUrl: '/images/builder/tyres/specialized_cannibal_placeholder.png', priceIndicator: 'upgrade' },
          { id: 'tires-specialized-butcher', price: 220, weightG: 2300, label: 'Specialized Butcher Grid Gravity T9', description: 'Versatile aggressive option in Grid Gravity casing with Gripton T9 compound. Front or rear.', material: 'alloy', size: '2.3 / 2.6"', finish: 'Black', imageUrl: '/images/builder/tyres/specialized_butcher_placeholder.png', priceIndicator: 'upgrade' },
          { id: 'tires-goodyear-newton-mtf', price: 240, weightG: 2400, label: 'Goodyear Newton MTF', description: 'Front-specific gravity tyre. Trail to Downhill casings with Grip3 and Grip3S compounds.', material: 'alloy', size: '2.5"', finish: 'Black', imageUrl: '/images/builder/tyres/goodyear_newton_mtf_placeholder.png', priceIndicator: 'upgrade' },
          { id: 'tires-goodyear-newton-mtr', price: 240, weightG: 2350, label: 'Goodyear Newton MTR', description: 'Rear-specific gravity tyre. Trail to Downhill casings with Grip3 and Grip3S compounds.', material: 'alloy', size: '2.4 / 2.6"', finish: 'Black', imageUrl: '/images/builder/tyres/goodyear_newton_mtr_placeholder.png', priceIndicator: 'upgrade' },
          { id: 'tires-wtb-verdict', price: 220, weightG: 2500, label: 'WTB Verdict', description: 'Niche aggressive front for loose terrain. High Grip TriTec compound, TCS Light/Tough and SG1/SG2 casings.', material: 'alloy', size: '2.5"', finish: 'Black', imageUrl: '/images/builder/tyres/wtb_verdict_placeholder.png', priceIndicator: 'upgrade' },
          { id: 'tires-wtb-judge', price: 220, weightG: 2600, label: 'WTB Judge', description: 'Heavy-duty enduro/DH rear. High Grip TriTec compound, TCS Light/Tough and SG1/SG2 casings.', material: 'alloy', size: '2.4"', finish: 'Black', imageUrl: '/images/builder/tyres/wtb_judge_placeholder.png', priceIndicator: 'upgrade' },
        ],
      },
    ],
  },

  // ─── Drivetrain ──────────────────────────────────────────────────────────────
  {
    id: 'drivetrain',
    label: 'Drivetrain',
    description: 'Gears — chain, cassette and derailleur — specified first, then the crank, chainring, pedals, and bottom bracket. Every rotating part matched to your terrain and riding style.',
    options: [],
    subcategories: [
      {
        id: 'groupset',
        label: 'Gears',
        options: [
          { id: 'groupset-sram-gx-eagle-axs', price: 1400, weightG: 2900, label: 'SRAM GX Eagle AXS Transmission', description: '12-speed wireless T-Type with 10–52T XS 1275 cassette and AXS Pod controller. The entry premium wireless option — requires a UDH frame and XD driver.', material: 'alloy', size: '12-speed / 10–52T', imageUrl: '/images/builder/drivetrain/sram-gx-eagle-transmission-side.png', priceIndicator: 'included' },
          { id: 'groupset-sram-x0-eagle-axs', price: 2600, weightG: 2650, label: 'SRAM X0 Eagle AXS Transmission', description: '12-speed wireless T-Type with 10–52T XS 1295 cassette and AXS Pod Ultimate. Tough, premium, and suited to aggressive enduro — the Burton default.', material: 'alloy', size: '12-speed / 10–52T', imageUrl: '/images/builder/drivetrain/sram-x0-eagle-transmission-side.png', priceIndicator: 'upgrade' },
          { id: 'groupset-sram-xx-eagle-axs', price: 3400, weightG: 2500, label: 'SRAM XX Eagle AXS Transmission', description: '12-speed wireless T-Type, 10–52T XS 1297 cassette, carbon cranks. Lighter and fancier than X0; power meter crank available as an upsell.', material: 'carbon', size: '12-speed / 10–52T', imageUrl: '/images/builder/drivetrain/sram-xx-eagle-transmission-side.png', priceIndicator: 'premium' },
          { id: 'groupset-sram-xx-sl-eagle-axs', price: 4200, weightG: 2350, label: 'SRAM XX SL Eagle AXS Transmission', description: 'Ultra-light 12-speed wireless race groupset with 34T ring and XS 1299 cassette. For down-country and lightweight builds — not a gravity spec.', material: 'carbon', size: '12-speed / 10–52T', imageUrl: '/images/builder/drivetrain/sram-xxsl-eagle-transmission-side.png', priceIndicator: 'premium' },
          { id: 'groupset-shimano-xtr-m9200-di2', price: 3800, weightG: 2600, label: 'Shimano XTR M9200 Di2 Wireless', description: '12-speed wireless XTR with 9–45T or 10–51T cassette and RD-M9250 derailleur. Hanger-based with Micro Spline freehub — the Shimano-loyalist flagship.', material: 'alloy', size: '12-speed / 10–51T', imageUrl: '/images/builder/drivetrain/shimano-xtr-m9200-di2-side.jpeg', priceIndicator: 'premium' },
        ],
      },
      {
        id: 'cranks',
        label: 'Cranks',
        options: [
          { id: 'cranks-hope-evo', price: 700, weightG: 620, label: 'Hope EVO Crankset', description: 'CNC-machined alloy cranks with 30mm 7075 axle, 135–175mm lengths. Anodised black, silver, bronze, blue, red, purple, or orange for colour-matched builds.', material: 'alloy', size: '135–175mm', finish: 'Anodised Colour Options', imageUrl: '/images/builder/drivetrain/hope-evo-crank-orange-side.png', priceIndicator: 'upgrade' },
          { id: 'cranks-5dev-rspec', price: 1100, weightG: 640, label: '5DEV R-Spec Trail/Enduro', description: '7050 billet aluminium cranks with SRAM 3-bolt direct mount, 135–170mm lengths. Very boutique — excellent for short-crank setups.', material: 'alloy', size: '135–170mm', finish: 'Matte Black / Matte Raw', imageUrl: '/images/builder/drivetrain/5dev-rspec-trail-enduro-crank-side.png', priceIndicator: 'premium' },
          { id: 'cranks-raceface-era-carbon', price: 800, weightG: 490, label: 'Race Face ERA Carbon', description: 'Carbon crank arms with stainless wear plates and 30mm Cinch spindle, 160–175mm. The clean lightweight choice where grams matter more than colour.', material: 'carbon', size: '160–175mm', finish: 'Black', imageUrl: '/images/builder/drivetrain/raceface-era-carbon-crank-side.png', priceIndicator: 'upgrade' },
        ],
      },
      {
        id: 'chainring',
        label: 'Chainring',
        options: [
          { id: 'chainring-ethirteen-guidering', price: 45, weightG: 70, label: 'e*thirteen Guidering Steel', description: 'Hardened steel narrow-wide direct-mount ring. 30–34T, SRAM 3-bolt. The hard-wearing gravity default — outlasts alloy on grit-heavy trails.', material: 'steel', size: '30–34T', finish: 'Black', imageUrl: '', priceIndicator: 'included' },
          { id: 'chainring-wolftooth-camo', price: 75, weightG: 55, label: 'Wolf Tooth CAMO Direct Mount', description: '7075-T6 alloy narrow-wide ring on the CAMO direct-mount spider. 30–36T, SRAM 3-bolt. Swap tooth count without changing the spider.', material: 'alloy', size: '30–36T', finish: 'Black', imageUrl: '', priceIndicator: 'upgrade' },
          { id: 'chainring-absoluteblack-oval', price: 90, weightG: 52, label: 'AbsoluteBLACK Oval N/W', description: 'Hard-anodised 7075 oval ring. The oval profile smooths the pedal stroke and improves traction on steep, loose climbs. 30–34T.', material: 'alloy', size: '30–34T', finish: 'Black / Red / Blue', imageUrl: '', priceIndicator: 'premium' },
          { id: 'chainring-garbaruk-round', price: 95, weightG: 48, label: 'Garbaruk Round Direct Mount', description: 'CNC-machined 7075 narrow-wide ring in seven anodised colours. 28–38T, SRAM 3-bolt. The colour-matching boutique choice.', material: 'alloy', size: '28–38T', finish: 'Anodised Colour Options', imageUrl: '', priceIndicator: 'premium' },
        ],
      },
      {
        id: 'pedals',
        label: 'Pedals',
        options: [
          { id: 'pedals-crankbrothers-stamp11', price: 350, weightG: 400, label: 'Crankbrothers Stamp 11', description: 'Large platform flat pedal. Adjustable pin length, concave profile, 101×114mm body. Grippy and durable across all conditions.', material: 'alloy', priceIndicator: 'upgrade' },
          { id: 'pedals-ht-x2', price: 250, weightG: 380, label: 'HT X2', description: 'CNC machined magnesium platform pedal. 10 pins per side, 104×105mm. Extremely thin profile — close-to-chassis feel for technical riding.', material: 'alloy', priceIndicator: 'premium' },
          { id: 'pedals-shimano-xtr-spd', price: 320, weightG: 310, label: 'Shimano XTR PD-M9100', description: 'Clipless SPD pedal. Widest platform in the XTR range, multi-release cleat. Efficient power transfer with reliable mud clearance.', material: 'alloy', priceIndicator: 'premium' },
          { id: 'pedals-crankbrothers-mallet', price: 300, weightG: 420, label: 'Crankbrothers Mallet E', description: 'Clipless pedal with large alloy platform surround. Easy entry and exit, 10mm float. The trail clipless favourite.', material: 'alloy', priceIndicator: 'upgrade' },
        ],
      },
      {
        id: 'bb',
        label: 'BB',
        options: [
          { id: 'bb-enduro-max', price: 250, weightG: 100, label: 'Enduro MAX Preload', description: 'External threaded BB with oversized ABEC-5 angular contact bearings. Preload system reduces creaking. Best durability in the category.', material: 'alloy', priceIndicator: 'upgrade' },
          { id: 'bb-shimano-bb93', price: 90, weightG: 90, label: 'Shimano BB93 HollowTech', description: 'OEM threaded external BB for HollowTech II cranks. Reliable, serviceable, and widely available. Pairs directly with Shimano cranks.', material: 'alloy', priceIndicator: 'included' },
          { id: 'bb-token-ninja', price: 300, weightG: 85, label: 'Token Ninja Threaded', description: 'Outboard threaded BB with ceramic hybrid bearings. Smooth, stiff, and significantly lighter than steel bearing equivalents.', material: 'alloy', priceIndicator: 'premium' },
        ],
      },
    ],
  },

  // ─── Cockpit ─────────────────────────────────────────────────────────────────
  {
    id: 'cockpit',
    label: 'Cockpit',
    description: 'Every contact point between you and the bike. Bars, grips, headset, stem, saddle, and seatpost configured individually.',
    options: [],
    subcategories: [
      {
        id: 'combined',
        label: 'Combined',
        visualOnly: true,
        options: [
          {
            id: 'combined-cockpit-visual',
            label: 'Combined Cockpit Visual',
            description: 'Admin-only visual layer for bars, grips, and headset rendered from one uploaded image.',
            material: 'alloy',
            priceIndicator: 'included',
            compatibleFrameIds: ['raptor-i', 'felon-i', 'typhoon-i', 'raider'],
          },
        ],
      },
      {
        id: 'bars',
        label: 'Bars',
        options: [
          { id: 'bars-renthal-fatbar-35', price: 320, weightG: 225, label: 'Renthal Fatbar 35 — 800mm', description: 'Carbon fibre handlebar, 35mm clamp, 800mm width. 20mm rise. Enduro benchmark — stiff and light with progressive sweep.', material: 'carbon', priceIndicator: 'upgrade' },
          { id: 'bars-chromag-fubars', price: 180, weightG: 320, label: 'Chromag Fubars OSX — 780mm', description: '7075 T6 alloy bar, 35mm clamp, 780mm. 9° backsweep, 5° upsweep. Trusted by enduro racers for strength and compliance.', material: 'alloy', priceIndicator: 'included' },
          { id: 'bars-renthal-fatbar-lite', price: 300, weightG: 200, label: 'Renthal Fatbar Lite 35 — 760mm', description: 'Carbon bar at a lighter weight class, 35mm clamp, 760mm. 10mm rise. Trail-to-XC builds where weight is prioritised.', material: 'carbon', priceIndicator: 'premium' },
          { id: 'bars-spank-vibrocore', price: 170, weightG: 350, label: 'Spank 35 Vibrocore — 800mm', description: 'Alloy bar with foam-injected Vibrocore vibration damping, 35mm, 800mm. Reduces hand fatigue significantly on rough terrain.', material: 'alloy', priceIndicator: 'upgrade' },
        ],
      },
      {
        id: 'grips',
        label: 'Grips',
        options: [
          { id: 'grips-ergon-gp1', price: 70, weightG: 190, label: 'Ergon GP1 Biokork', description: 'Lock-on cork compound grip with ergonomic wing. Reduces hand fatigue over long rides. Tactile and comfortable on enduro paced days.', material: 'alloy', priceIndicator: 'included' },
          { id: 'grips-odt-elite', price: 55, weightG: 110, label: 'ODI Elite Pro Lock-On', description: 'Dual-compound lock-on grip. 130mm length, aggressive tread pattern for wet and dry. Reliable enduro and trail favourite.', material: 'alloy', priceIndicator: 'included' },
          { id: 'grips-burgtec-bartender', price: 60, weightG: 105, label: 'Burgtec Bartender MK3', description: 'Medium compound rubber, 31mm diameter, thin-wall aluminium clamp collar. Tacky feel without being overly soft — precise feedback.', material: 'alloy', priceIndicator: 'upgrade' },
        ],
      },
      {
        id: 'headset',
        label: 'Headset',
        options: [
          { id: 'headset-chris-king-i7', price: 350, weightG: 100, label: 'Chris King i7', description: 'Integrated threadless headset. RingDrive bearings, 7-colour anodised options, lifetime warranty. The definitive quality benchmark.', material: 'alloy', priceIndicator: 'premium' },
          { id: 'headset-cane-creek-40', price: 110, weightG: 120, label: 'Cane Creek 40', description: 'Zero-stack threadless headset. ACB angular contact bearings, full cartridge. Reliable, easy to service, and broadly compatible.', material: 'alloy', priceIndicator: 'included' },
          { id: 'headset-hope-integrated', price: 220, weightG: 105, label: 'Hope Integrated', description: 'CNC machined 7075 alloy cups, sealed bearings, stainless hardware. Precision British engineering — multiple anodised colours available.', material: 'alloy', priceIndicator: 'upgrade' },
        ],
      },
      {
        id: 'stem',
        label: 'Stem',
        options: [
          { id: 'stem-burton-ti-50', price: 450, weightG: 130, label: 'Burton Ti Stem 50mm', description: 'In-house machined 6Al-4V titanium stem. 35mm clamp, ±6° rise. Shorter cockpit setup — aggressive position for steep terrain.', material: 'titanium', priceIndicator: 'included' },
          { id: 'stem-burton-ti-70', price: 450, weightG: 145, label: 'Burton Ti Stem 70mm', description: 'The most common Burton stem length. Same precision titanium construction — ideal for riders 170–185cm in an enduro position.', material: 'titanium', priceIndicator: 'included' },
          { id: 'stem-renthal-apex', price: 220, weightG: 130, label: 'Renthal Apex 35 — 50mm', description: 'Forged 6061 alloy, 35mm clamp, 50mm, 0° rise. High-stiffness race stem with minimalist profile.', material: 'alloy', priceIndicator: 'upgrade' },
          { id: 'stem-deity-copperhead', price: 180, weightG: 135, label: 'Deity Copperhead 35 — 35mm', description: 'Forged 6061, ultra-short 35mm stem. Aggressive DH and park geometry. Maximum control and steering precision.', material: 'alloy', priceIndicator: 'upgrade' },
        ],
      },
      {
        id: 'seat',
        label: 'Seat',
        options: [
          { id: 'seat-wtb-silverado', price: 120, weightG: 270, label: 'WTB Silverado', description: 'Narrow-nosed trail saddle. Padding optimised for both standing and seated positions. Chromoly rails. The versatile enduro standard.', material: 'alloy', priceIndicator: 'included' },
          { id: 'seat-ergon-sm-pro-ti', price: 260, weightG: 220, label: 'Ergon SM Pro Titanium', description: 'Trail saddle with titanium rails. Anatomic channel, firm padding, 277mm length. Light and confidence-inspiring over long days.', material: 'titanium', priceIndicator: 'upgrade' },
          { id: 'seat-fizik-taiga-mkii', price: 280, weightG: 240, label: 'Fizik Taiga Mkii', description: 'Enduro-specific saddle with reinforced shell and carbon-reinforced rails. Flat profile, durable construction for aggressive riding.', material: 'carbon', priceIndicator: 'premium' },
          { id: 'seat-sdg-bel-air', price: 220, weightG: 200, label: 'SDG Bel-Air', description: 'Lightweight race saddle with titanium rails. Narrow profile and minimal padding — for riders who prefer a fast, unobtrusive perch.', material: 'titanium', priceIndicator: 'upgrade' },
        ],
      },
      {
        id: 'post',
        label: 'Post',
        options: [
          { id: 'post-bikeyoke-revive', price: 700, weightG: 600, label: 'BikeYoke Revive 2.0', description: 'Field-serviceable dropper post. 185mm drop (matched to frame), infinite position lock, no bleed required. The serviceability gold standard.', material: 'alloy', priceIndicator: 'upgrade' },
          { id: 'post-rockshox-reverb-axs', price: 1300, weightG: 650, label: 'RockShox Reverb AXS', description: 'Wireless electronic dropper. 170mm drop, programmable actuation, integrates with SRAM AXS ecosystem.', material: 'alloy', priceIndicator: 'premium' },
          { id: 'post-burton-ti', price: 380, weightG: 280, label: 'Burton Ti Seatpost', description: 'Fixed-length 3Al-2.5V titanium seatpost, 31.6mm. For hardtail or XC builds where a rigid post is preferred.', material: 'titanium', priceIndicator: 'included' },
        ],
      },
    ],
  },

  // ─── Brakes ──────────────────────────────────────────────────────────────────
  {
    id: 'brakes',
    label: 'Brakes',
    description: 'Complete brake system and rotor specified separately. Rotor size is constrained by frame, fork clearance, and riding discipline at order.',
    options: [],
    subcategories: [
      {
        id: 'brakeset',
        label: 'Brake System',
        options: [
          { id: 'brakeset-sram-maven-ultimate', price: 1200, weightG: 700, label: 'SRAM Maven Ultimate', description: 'Big-power 4-piston mineral-oil gravity brake with tool-free reach and contact adjust. Pairs with HS2 rotors up to 220mm. The top SRAM option.', material: 'alloy', size: '4-piston', finish: 'Clear Anodised / Silver', imageUrl: '/images/builder/brakes/sram_maven_ultimate.png', priceIndicator: 'premium' },
          { id: 'brakeset-hope-tech-4-v4', price: 900, weightG: 640, label: 'Hope Tech 4 V4', description: 'CNC-machined 4-piston DH/enduro brake on DOT 5.1 with tool-free reach and bite point adjust. The best colour-matching option — six accent colours.', material: 'alloy', size: '4-piston', finish: 'Black / Silver + Anodised Accents', imageUrl: '/images/builder/brakes/hope_tech_4_v4.png', priceIndicator: 'premium' },
          { id: 'brakeset-hayes-dominion-a4', price: 700, weightG: 620, label: 'Hayes Dominion A4', description: '4-piston brake with four 17mm pistons and factory-tuned short dead stroke. ~310g. Outstanding lever feel and modulation over flash.', material: 'alloy', size: '4-piston', finish: 'Stealth Black / Bronze', imageUrl: '/images/builder/brakes/hayes_dominion_a4.png', priceIndicator: 'upgrade' },
          { id: 'brakeset-trp-dh-r-evo', price: 650, weightG: 660, label: 'TRP DH-R EVO', description: 'Gravity-focused 4-piston brake designed around thick 2.3mm rotors. Simple, powerful spec for DH, e-bike, and park builds.', material: 'alloy', size: '4-piston', finish: 'Gold / Silver / Black', imageUrl: '/images/builder/brakes/trp_dh_r_evo.png', priceIndicator: 'upgrade' },
          { id: 'brakeset-shimano-xtr-m9120', price: 900, weightG: 560, label: 'Shimano XTR BR-M9120', description: '4-piston XTR with ceramic pistons, Servo Wave lever, and Ice Tech finned pads. Clean, light, and reliable — the premium Shimano choice.', material: 'alloy', size: '4-piston', finish: 'XTR Black / Silver', imageUrl: '/images/builder/brakes/shimano_xtr_m9120.png', priceIndicator: 'upgrade' },
          { id: 'brakeset-magura-mt7-pro', price: 750, weightG: 510, label: 'Magura MT7 Pro', description: 'Four 17mm pistons with radial master cylinder on Royal Blood mineral oil. ~255g. Very powerful and punchy; Raceline yellow available as a colour option.', material: 'alloy', size: '4-piston', finish: 'Black / Raceline Yellow', imageUrl: '/images/builder/brakes/magura_mt7_pro.png', priceIndicator: 'upgrade' },
          { id: 'brakeset-hope-tech-4-e4', price: 850, weightG: 580, label: 'Hope Tech 4 E4', description: 'Lighter 4-piston Hope for trail and enduro. Same tool-free reach and bite adjust and anodised colour matching as the V4, less outright power.', material: 'alloy', size: '4-piston', finish: 'Black / Silver + Anodised Accents', imageUrl: '/images/builder/brakes/hope_tech_4_e4.png', priceIndicator: 'upgrade' },
          { id: 'brakeset-trickstuff-maxima', price: 2400, weightG: 480, label: 'Trickstuff Maxima', description: 'The no-budget halo brake. Hand-built German 4-piston on Bionol with Goodridge hose options. Expensive, niche, and perfect for a dream build.', material: 'alloy', size: '4-piston', finish: 'Black / Silver', imageUrl: '/images/builder/brakes/trickstuff_maxima.png', priceIndicator: 'premium' },
        ],
      },
      {
        id: 'rotor',
        label: 'Rotor',
        options: [
          { id: 'rotor-sram-hs2', price: 180, weightG: 440, label: 'SRAM HS2', description: 'MTB-specific 2.0mm rotor with improved heat management and power. Pairs with Maven and Code. 6-bolt and Centre-Lock.', material: 'steel', size: '180 / 200 / 220mm', priceIndicator: 'included' },
          { id: 'rotor-hope-floating', price: 260, weightG: 380, label: 'Hope Floating Rotor', description: 'Two-piece 1.8mm floating rotor with anodised colour centres. The best visual pairing for Hope Tech 4 brakes. 6-bolt.', material: 'steel', size: '160–220mm', priceIndicator: 'upgrade' },
          { id: 'rotor-hayes-d-series', price: 160, weightG: 420, label: 'Hayes D-Series', description: '1.95mm rotor with QuickBite profile, matched to the Dominion A4. 6-bolt.', material: 'steel', size: '160 / 180 / 203 / 220mm', priceIndicator: 'included' },
          { id: 'rotor-trp-r1-evo', price: 200, weightG: 500, label: 'TRP R1 / EVO 2.3mm', description: 'Thick 2.3mm rotor system for DH and e-MTB heat and stiffness. Designed for the DH-R EVO. 6-bolt.', material: 'steel', size: '180 / 203 / 223mm', priceIndicator: 'upgrade' },
          { id: 'rotor-shimano-rt-mt900', price: 300, weightG: 300, label: 'Shimano RT-MT900 Ice Tech Freeza', description: 'Premium XTR rotor with Freeza cooling fins for sustained braking. Centre-Lock.', material: 'steel', size: '160 / 180 / 203mm', priceIndicator: 'upgrade' },
          { id: 'rotor-magura-mdr-p', price: 220, weightG: 480, label: 'Magura MDR-P', description: 'Heavy-duty 2.0mm rotor for high braking loads, matched to the MT7. 6-bolt and Centre-Lock.', material: 'steel', size: '180 / 203 / 220mm', priceIndicator: 'upgrade' },
        ],
      },
    ],
  },

]

/**
 * Lookup a category by ID. Returns undefined if not found.
 */
export function getCategoryById(id: string): PartCategory | undefined {
  return PARTS.find((cat) => cat.id === id)
}

/** Tyres are not frame-constrained in the source spreadsheets — available on every model. */
const ALL_TYRE_IDS = [
  'tires-maxxis-assegai',
  'tires-maxxis-minion-dhf',
  'tires-maxxis-dhr2',
  'tires-maxxis-dissector',
  'tires-maxxis-shorty',
  'tires-maxxis-aggressor',
  'tires-conti-kryptotal-fr',
  'tires-conti-kryptotal-re',
  'tires-conti-argotal',
  'tires-conti-xynotal',
  'tires-schwalbe-magic-mary',
  'tires-schwalbe-big-betty',
  'tires-vittoria-mazza',
  'tires-vittoria-martello',
  'tires-vittoria-mota',
  'tires-specialized-cannibal',
  'tires-specialized-butcher',
  'tires-goodyear-newton-mtf',
  'tires-goodyear-newton-mtr',
  'tires-wtb-verdict',
  'tires-wtb-judge',
]

const COMPATIBLE_OPTION_IDS: Record<string, Set<string>> = {
  // Felon — downhill / park. DH spacing, coil-friendly, big rotors.
  'felon-i': new Set([
    'fork-rockshox-boxxer-ultimate',
    'shock-fox-float-x2-factory',
    'shock-fox-dhx2-factory',
    'shock-rockshox-vivid-ultimate',
    'shock-ohlins-ttx22m-coil',
    'shock-ext-storia-v4',
    'shock-cane-creek-kitsuma-coil',
    'shock-dvo-jade-x',
    'wheelset-reserve-31dh',
    'wheelset-enve-m9-pro',
    'wheelset-raceface-era',
    ...ALL_TYRE_IDS,
    'groupset-sram-gx-eagle-axs',
    'groupset-sram-x0-eagle-axs',
    'groupset-sram-xx-eagle-axs',
    'groupset-shimano-xtr-m9200-di2',
    'cranks-hope-evo',
    'cranks-5dev-rspec',
    'pedals-crankbrothers-stamp11',
    'pedals-ht-x2',
    'pedals-crankbrothers-mallet',
    'bb-enduro-max',
    'bb-token-ninja',
    'bars-renthal-fatbar-35',
    'bars-chromag-fubars',
    'bars-spank-vibrocore',
    'grips-odt-elite',
    'grips-burgtec-bartender',
    'headset-chris-king-i7',
    'headset-hope-integrated',
    'stem-renthal-apex',
    'stem-deity-copperhead',
    'seat-fizik-taiga-mkii',
    'seat-sdg-bel-air',
    'post-bikeyoke-revive',
    'brakeset-sram-maven-ultimate',
    'brakeset-hope-tech-4-v4',
    'brakeset-hayes-dominion-a4',
    'brakeset-trp-dh-r-evo',
    'brakeset-magura-mt7-pro',
    'brakeset-trickstuff-maxima',
    'rotor-sram-hs2',
    'rotor-hope-floating',
    'rotor-hayes-d-series',
    'rotor-trp-r1-evo',
    'rotor-magura-mdr-p',
  ]),
  // Raptor — the main custom enduro bike. Full premium range.
  'raptor-i': new Set([
    'fork-fox-38-factory-gripx2',
    'fork-rockshox-zeb-ultimate',
    'fork-fox-36-factory-gripx2',
    'fork-rockshox-lyrik-ultimate',
    'shock-fox-float-x2-factory',
    'shock-fox-dhx2-factory',
    'shock-rockshox-vivid-ultimate',
    'shock-rockshox-super-deluxe-ultimate',
    'shock-ohlins-ttx22m-coil',
    'shock-ohlins-ttx2air',
    'shock-ext-storia-v4',
    'shock-cane-creek-kitsuma-coil',
    'shock-dvo-jade-x',
    'wheelset-nobl-tr37',
    'wheelset-nobl-tr41',
    'wheelset-reserve-30hd',
    'wheelset-reserve-30hd-31dh',
    'wheelset-wao-triad',
    'wheelset-i9-hydra-enduro-s',
    'wheelset-enve-am30',
    'wheelset-dt-exc-1200',
    'wheelset-dt-xmc-1200',
    'wheelset-cb-synthesis-enduro',
    'wheelset-zipp-3zero-moto',
    'wheelset-roval-traverse-hd',
    'wheelset-raceface-era',
    'wheelset-raceface-turbine',
    ...ALL_TYRE_IDS,
    'groupset-sram-gx-eagle-axs',
    'groupset-sram-x0-eagle-axs',
    'groupset-sram-xx-eagle-axs',
    'groupset-shimano-xtr-m9200-di2',
    'cranks-hope-evo',
    'cranks-5dev-rspec',
    'cranks-raceface-era-carbon',
    'pedals-crankbrothers-stamp11',
    'pedals-ht-x2',
    'pedals-shimano-xtr-spd',
    'pedals-crankbrothers-mallet',
    'bb-enduro-max',
    'bb-shimano-bb93',
    'bb-token-ninja',
    'bars-renthal-fatbar-35',
    'bars-chromag-fubars',
    'bars-renthal-fatbar-lite',
    'bars-spank-vibrocore',
    'grips-ergon-gp1',
    'grips-odt-elite',
    'grips-burgtec-bartender',
    'headset-chris-king-i7',
    'headset-cane-creek-40',
    'headset-hope-integrated',
    'stem-burton-ti-50',
    'stem-burton-ti-70',
    'stem-renthal-apex',
    'stem-deity-copperhead',
    'seat-wtb-silverado',
    'seat-ergon-sm-pro-ti',
    'seat-fizik-taiga-mkii',
    'seat-sdg-bel-air',
    'post-bikeyoke-revive',
    'post-rockshox-reverb-axs',
    'brakeset-sram-maven-ultimate',
    'brakeset-hope-tech-4-v4',
    'brakeset-hayes-dominion-a4',
    'brakeset-shimano-xtr-m9120',
    'brakeset-magura-mt7-pro',
    'brakeset-hope-tech-4-e4',
    'brakeset-trickstuff-maxima',
    'rotor-sram-hs2',
    'rotor-hope-floating',
    'rotor-hayes-d-series',
    'rotor-shimano-rt-mt900',
    'rotor-magura-mdr-p',
  ]),
  // Typhoon — down-country / fast trail. Light and fast, never overbuilt.
  'typhoon-i': new Set([
    'fork-fox-36-factory-gripx2',
    'fork-rockshox-lyrik-ultimate',
    'fork-rockshox-pike-ultimate',
    'shock-rockshox-super-deluxe-ultimate',
    'shock-ohlins-ttx2air',
    'wheelset-i9-solix-xc',
    'wheelset-dt-xmc-1200',
    'wheelset-enve-am30',
    ...ALL_TYRE_IDS,
    'groupset-sram-xx-eagle-axs',
    'groupset-sram-xx-sl-eagle-axs',
    'groupset-shimano-xtr-m9200-di2',
    'cranks-hope-evo',
    'cranks-raceface-era-carbon',
    'pedals-crankbrothers-stamp11',
    'pedals-shimano-xtr-spd',
    'pedals-crankbrothers-mallet',
    'bb-shimano-bb93',
    'bb-token-ninja',
    'bars-renthal-fatbar-lite',
    'bars-chromag-fubars',
    'grips-ergon-gp1',
    'grips-odt-elite',
    'headset-chris-king-i7',
    'headset-cane-creek-40',
    'stem-burton-ti-70',
    'seat-wtb-silverado',
    'seat-ergon-sm-pro-ti',
    'post-bikeyoke-revive',
    'post-rockshox-reverb-axs',
    'post-burton-ti',
    'brakeset-shimano-xtr-m9120',
    'brakeset-hope-tech-4-e4',
    'rotor-shimano-rt-mt900',
    'rotor-hope-floating',
  ]),
  // Raider — enduro e-bike. E-bike-rated wheels, shocks, and big-power brakes.
  raider: new Set([
    'fork-fox-38-factory-gripx2',
    'fork-rockshox-zeb-ultimate',
    'shock-fox-float-x2-factory',
    'shock-fox-dhx2-factory',
    'shock-rockshox-vivid-ultimate',
    'shock-rockshox-super-deluxe-ultimate',
    'shock-cane-creek-kitsuma-coil',
    'shock-dvo-jade-x',
    'wheelset-nobl-tr41',
    'wheelset-reserve-30hd',
    'wheelset-reserve-30hd-31dh',
    'wheelset-dt-hxc-1501',
    'wheelset-roval-traverse-hd',
    'wheelset-raceface-era',
    ...ALL_TYRE_IDS,
    'groupset-sram-gx-eagle-axs',
    'groupset-sram-x0-eagle-axs',
    'groupset-shimano-xtr-m9200-di2',
    'cranks-hope-evo',
    'cranks-5dev-rspec',
    'pedals-crankbrothers-stamp11',
    'pedals-ht-x2',
    'pedals-crankbrothers-mallet',
    'bb-enduro-max',
    'bb-token-ninja',
    'bars-renthal-fatbar-35',
    'bars-chromag-fubars',
    'bars-spank-vibrocore',
    'grips-ergon-gp1',
    'grips-odt-elite',
    'grips-burgtec-bartender',
    'headset-chris-king-i7',
    'headset-hope-integrated',
    'stem-renthal-apex',
    'stem-deity-copperhead',
    'seat-wtb-silverado',
    'seat-fizik-taiga-mkii',
    'post-bikeyoke-revive',
    'post-rockshox-reverb-axs',
    'brakeset-sram-maven-ultimate',
    'brakeset-trp-dh-r-evo',
    'rotor-sram-hs2',
    'rotor-trp-r1-evo',
  ]),
}

export function getPartsForFrameFromCatalog(
  parts: PartCategory[],
  frameId: string | null | undefined
): PartCategory[] {
  if (!frameId) return parts
  const allowed = COMPATIBLE_OPTION_IDS[frameId]
  if (!allowed) return parts
  const isAllowed = (option: { id: string; compatibleFrameIds?: string[] }) =>
    option.compatibleFrameIds ? option.compatibleFrameIds.includes(frameId) : allowed.has(option.id)

  return parts.map((category) => {
    if (category.subcategories) {
      return {
        ...category,
        subcategories: category.subcategories.map((sub) => ({
          ...sub,
          options: sub.options.filter(isAllowed),
        })),
      }
    }

    return {
      ...category,
      options: category.options.filter(isAllowed),
    }
  })
}

export function getPartsForFrame(frameId: string | null | undefined): PartCategory[] {
  return getPartsForFrameFromCatalog(PARTS, frameId)
}
